// Compat shim mapped via Vite alias `@mui/styles` → this file.
// Lets the ~43 legacy files keep their `import { makeStyles, withStyles } from '@mui/styles'`
// imports while the underlying engine is tss-react/mui on top of MUI v7 + Emotion.
//
// makeStyles(stylesOrFn) returns a hook that returns the classes map directly
// (matching the old @mui/styles signature — NOT the tss-react default of { classes }).
// withStyles(stylesOrFn)(Component) wraps a component and injects `classes` as a prop.

import { createElement, forwardRef, type ComponentType } from 'react';
import { makeStyles as tssMakeStyles } from 'tss-react/mui';
// v7 `styled` from @mui/material/styles is the natural replacement for v4's
// `@mui/styles` styled — both accept either (Component) or (tag) and return a
// styled element. Call sites are compatible.
export { styled } from '@mui/material/styles';

type Theme = any;
type StylesArg = Record<string, any> | ((theme: Theme) => Record<string, any>);

const buildHook = (styles: StylesArg) =>
  tssMakeStyles<any>()((theme: Theme) =>
    (typeof styles === 'function' ? styles(theme) : styles) as any
  );

export function makeStyles<P = any>(styles: StylesArg, _options?: any) {
  const useTss = buildHook(styles);
  return function useStyles(props?: P) {
    const { classes } = useTss((props ?? ({} as any)) as any);
    return classes as Record<string, string>;
  };
}

export function withStyles(styles: StylesArg, _options?: any) {
  const useTss = buildHook(styles);
  return function wrap<P extends { classes?: any }>(Component: ComponentType<P>) {
    const Wrapped = forwardRef<unknown, P>((props, ref) => {
      // tss-react's hook expects a params arg when the slice is typed `<any>`;
      // withStyles has no params, so pass an empty object.
      const { classes } = useTss({});
      return createElement(Component as any, { ...(props as any), classes, ref });
    });
    Wrapped.displayName = `withStyles(${Component.displayName || Component.name || 'Component'})`;
    return Wrapped as unknown as ComponentType<P>;
  };
}

export const createStyles = <T extends StylesArg>(s: T): T => s;

export const StylesProvider = ({ children }: { children: any }) => children;

export default { makeStyles, withStyles, createStyles, StylesProvider };
